import { useState } from "react";
import { toast } from "sonner";
import { submitRsvp } from "@/lib/api";
import useReveal from "@/lib/useReveal";
import { useSection } from "@/lib/SiteContent";

const initial = {
  name: "",
  attending: "ja",
  guests: 1,
  guest_names: [],
  allergies: "",
  song_wish: "",
  email: "",
  message: "",
};

// Shared style tokens — light cream tones on the dark teal background
const LABEL = "block text-[10px] uppercase tracking-[0.3em] text-[#DCE2C6] mb-3";
const INPUT =
  "w-full bg-transparent border-b border-[#DCE2C6]/50 py-3 text-[#F3F5E7] focus:outline-none focus:border-[#F3F5E7] transition-colors font-light placeholder:text-[#DCE2C6]/40";

export const Rsvp = () => {
  const { deadline } = useSection("rsvp", { deadline: "1. juli" });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const ref = useReveal();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateGuests = (raw) => {
    const n = Math.max(1, Math.min(10, Number(raw) || 1));
    setForm((f) => {
      const cur = f.guest_names || [];
      const need = Math.max(0, n - 1);
      const next = cur.slice(0, need);
      while (next.length < need) next.push("");
      return { ...f, guests: n, guest_names: next };
    });
  };

  const updateGuestName = (idx, value) => {
    setForm((f) => {
      const next = [...(f.guest_names || [])];
      next[idx] = value;
      return { ...f, guest_names: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Vennligst skriv inn navnet ditt.");
      return;
    }
    setLoading(true);
    try {
      await submitRsvp({
        ...form,
        guests: Number(form.guests) || 1,
        guest_names: (form.guest_names || []).map((s) => s.trim()).filter(Boolean),
      });
      setSubmitted(true);
      toast.success("Tusen takk for svaret ditt!");
    } catch (err) {
      console.error(err);
      toast.error("Noe gikk galt. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const extras = Math.max(0, Number(form.guests || 1) - 1);

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#3D5E68] text-[#F3F5E7]"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="text-xs uppercase tracking-[0.35em] text-[#DCE2C6] mb-6 font-medium">
            Kapittel VI
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.1]">
            Si fra om du kommer
          </h2>
          <div className="flex items-center justify-center mt-8">
            <span className="ornament" />
          </div>
          <p className="mt-8 text-base md:text-lg text-[#EAEDDC] font-light leading-relaxed">
            Vennligst svar innen <em className="font-serif">{deadline}</em>.
          </p>
        </div>

        {submitted ? (
          <div
            ref={ref}
            data-testid="rsvp-success"
            className="reveal text-center border border-[#DCE2C6]/50 py-16 px-8"
          >
            <p className="font-serif text-3xl md:text-4xl mb-6">Tusen takk!</p>
            <p className="text-[#EAEDDC] font-light leading-relaxed max-w-lg mx-auto">
              Vi har mottatt svaret ditt og gleder oss enormt til å feire med deg.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm(initial);
              }}
              data-testid="rsvp-reset-button"
              className="mt-10 px-8 py-3 border border-[#F3F5E7] text-xs uppercase tracking-[0.25em] text-[#F3F5E7] hover:bg-[#F3F5E7] hover:text-[#3D5E68] transition-colors duration-500"
            >
              Send et nytt svar
            </button>
          </div>
        ) : (
          <form
            ref={ref}
            onSubmit={onSubmit}
            data-testid="rsvp-form"
            className="reveal space-y-10"
          >
            <div>
              <label className={LABEL}>Navn</label>
              <input
                data-testid="rsvp-name-input"
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
                className={INPUT}
                placeholder="Ditt fulle navn"
              />
            </div>

            <div>
              <label className={`${LABEL} mb-4`}>Kommer du?</label>
              <div className="flex gap-4">
                {[
                  { value: "ja", label: "Ja, jeg kommer" },
                  { value: "nei", label: "Beklager, kan ikke" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`rsvp-attending-${opt.value}`}
                    onClick={() => update("attending", opt.value)}
                    className={`flex-1 px-6 py-4 border text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                      form.attending === opt.value
                        ? "bg-[#F3F5E7] text-[#3D5E68] border-[#F3F5E7]"
                        : "border-[#DCE2C6]/50 text-[#F3F5E7] hover:border-[#F3F5E7]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {form.attending === "ja" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className={LABEL}>Antall personer</label>
                    <input
                      data-testid="rsvp-guests-input"
                      type="number"
                      min="1"
                      max="10"
                      value={form.guests}
                      onChange={(e) => updateGuests(e.target.value)}
                      className={INPUT}
                    />
                  </div>
                  <div>
                    <label className={LABEL}>E-post</label>
                    <input
                      data-testid="rsvp-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      className={INPUT}
                      placeholder="navn@email.no"
                    />
                  </div>
                </div>

                {extras > 0 && (
                  <div className="space-y-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#DCE2C6]">
                      Navn på øvrige gjester
                    </p>
                    {Array.from({ length: extras }).map((_, i) => (
                      <div key={i}>
                        <label className={LABEL}>
                          Gjest {i + 2}
                        </label>
                        <input
                          data-testid={`rsvp-guest-name-${i}`}
                          type="text"
                          value={(form.guest_names || [])[i] || ""}
                          onChange={(e) => updateGuestName(i, e.target.value)}
                          className={INPUT}
                          placeholder="Fullt navn"
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label className={LABEL}>Allergier eller spesielle hensyn</label>
                  <input
                    data-testid="rsvp-allergies-input"
                    type="text"
                    value={form.allergies}
                    onChange={(e) => update("allergies", e.target.value)}
                    className={INPUT}
                    placeholder="F.eks. vegetar, gluten, nøtter..."
                  />
                </div>
              </>
            )}

            <div>
              <label className={LABEL}>En hilsen (valgfritt)</label>
              <textarea
                data-testid="rsvp-message-input"
                rows="3"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className={`${INPUT} resize-none`}
                placeholder="Skriv noen ord til brudeparet..."
              />
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                data-testid="rsvp-submit-button"
                className="w-full md:w-auto px-12 py-4 bg-[#68919B] text-[#F3F5E7] text-xs uppercase tracking-[0.3em] hover:bg-[#5A7682] transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sender…" : "Send svaret mitt"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
